import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Paper,
  Stack,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  LinearProgress,
  useTheme,
  alpha,
  Skeleton,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Tabs,
  Tab,
  Tooltip,
  Badge,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CreditCard as CardIcon,
  Business as BusinessIcon,
  CheckCircle as ActiveIcon,
  Schedule as TrialIcon,
  Block as SuspendedIcon,
  Cancel as CanceledIcon,
  ArrowUpward,
  ArrowDownward,
  Receipt as InvoiceIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  CalendarToday,
  Assessment,
  AccountBalance,
  ShowChart,
  PieChart,
  Timeline,
  Groups,
  TrendingDown,
  Info as InfoIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  CreditScore,
  MoneyOff,
  Webhook,
} from '@mui/icons-material';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

interface BillingOverview {
  mrr: number;
  arr: number;
  activeSubscriptions: number;
  totalTenants: number;
  churnRate: number;
  revenueByPlan: {
    starter: number;
    growth: number;
    professional: number;
    enterprise: number;
    custom: number;
  };
  growth: {
    new: number;
    churned: number;
    expansion: number;
    contraction: number;
  };
}

interface PlatformSubscription {
  _id: string;
  tenantId: {
    _id: string;
    name: string;
    subdomain: string;
    status: string;
  };
  plan: string;
  status: string;
  billing: {
    amount: number;
    interval: string;
    currency: string;
    totalAmount: number;
  };
  schedule: {
    nextBillingDate: string;
    trialEndDate?: string;
  };
  stripe?: {
    subscriptionId: string;
    customerId: string;
  };
  payments: Array<{
    amount: number;
    status: string;
    processedAt: string;
  }>;
  createdAt: string;
}

interface Invoice {
  _id: string;
  invoiceNumber: string;
  amount: number;
  total: number;
  status: string;
  dueDate: string;
  paidAt?: string;
  stripeInvoiceId?: string;
  pdfUrl?: string;
}

interface MetricCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
  subtitle?: string;
  trend?: number;
  onClick?: () => void;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color, subtitle, trend, onClick }) => {
  const theme = useTheme();
  
  return (
    <Card 
      sx={{ 
        height: '100%',
        transition: 'all 0.3s',
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
        }
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            <Typography color="text.secondary" variant="body2" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight="600" sx={{ mb: 1 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              bgcolor: alpha(color, 0.1),
              color: color,
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {icon}
          </Box>
        </Box>
        {trend !== undefined && (
          <Box display="flex" alignItems="center" mt={2}>
            {trend >= 0 ? (
              <ArrowUpward sx={{ color: 'success.main', fontSize: 16, mr: 0.5 }} />
            ) : (
              <ArrowDownward sx={{ color: 'error.main', fontSize: 16, mr: 0.5 }} />
            )}
            <Typography 
              variant="caption" 
              color={trend >= 0 ? 'success.main' : 'error.main'}
              fontWeight="500"
            >
              {Math.abs(trend)}% from last month
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const PlatformBillingDashboard: React.FC = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [overview, setOverview] = useState<BillingOverview | null>(null);
  const [subscriptions, setSubscriptions] = useState<PlatformSubscription[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (user?.isPlatformAdmin) {
      fetchBillingData();
    }
  }, [user, statusFilter, planFilter]);

  const fetchBillingData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Fetch billing overview
      const overviewResponse = await api.get('/platform/billing/overview');
      setOverview(overviewResponse.data);
      
      // Fetch subscriptions
      const query = new URLSearchParams();
      if (statusFilter !== 'all') query.append('status', statusFilter);
      if (planFilter !== 'all') query.append('plan', planFilter);
      
      const subscriptionsResponse = await api.get(`/platform/billing/subscriptions?${query}`);
      setSubscriptions(subscriptionsResponse.data.subscriptions || []);
      
    } catch (err: any) {
      console.error('Error fetching billing data:', err);
      setError(err.response?.data?.error || 'Failed to load billing data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <ActiveIcon sx={{ fontSize: 20, color: 'success.main' }} />;
      case 'trialing':
        return <TrialIcon sx={{ fontSize: 20, color: 'info.main' }} />;
      case 'past_due':
        return <WarningIcon sx={{ fontSize: 20, color: 'warning.main' }} />;
      case 'canceled':
        return <CanceledIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      case 'suspended':
        return <SuspendedIcon sx={{ fontSize: 20, color: 'error.main' }} />;
      default:
        return <BusinessIcon sx={{ fontSize: 20 }} />;
    }
  };

  const getStatusColor = (status: string): any => {
    const colors: Record<string, string> = {
      active: 'success',
      trialing: 'info',
      past_due: 'warning',
      canceled: 'error',
      suspended: 'error',
    };
    return colors[status] || 'default';
  };

  const getPlanColor = (plan: string): any => {
    const colors: Record<string, string> = {
      starter: 'info',
      growth: 'primary',
      professional: 'secondary',
      enterprise: 'warning',
      custom: 'default',
    };
    return colors[plan] || 'default';
  };

  const getPlanColorHex = (plan: string): string => {
    const colors: Record<string, string> = {
      starter: '#0288d1',  // info color
      growth: '#1976d2',   // primary color
      professional: '#dc004e', // secondary color
      enterprise: '#ed6c02',   // warning color
      custom: '#757575',       // default grey
    };
    return colors[plan] || '#757575';
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleExport = () => {
    // TODO: Implement export functionality
    console.log('Export billing data');
  };

  const handleViewSubscription = (subscriptionId: string) => {
    // TODO: Navigate to subscription details
    console.log('View subscription:', subscriptionId);
  };

  const handleManagePayment = (subscriptionId: string) => {
    // TODO: Open payment management modal
    console.log('Manage payment:', subscriptionId);
  };

  if (!user?.isPlatformAdmin) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 4 }}>
          Access denied. Platform admin privileges required.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ mb: 4 }}>
          Platform Billing Dashboard
        </Typography>
        <Grid container spacing={3}>
          {[1, 2, 3, 4].map((i) => (
            <Grid item xs={12} sm={6} lg={3} key={i}>
              <Skeleton variant="rectangular" height={160} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Container>
    );
  }

  if (error) {
    return (
      <Container sx={{ py: 4 }}>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={fetchBillingData}>
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" fontWeight="600" gutterBottom>
            Platform Billing Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitor revenue, subscriptions, and billing metrics across all tenants
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <IconButton onClick={fetchBillingData} color="primary">
            <RefreshIcon />
          </IconButton>
          <Button
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExport}
          >
            Export
          </Button>
          <Button
            variant="contained"
            startIcon={<Webhook />}
            href={`${window.location.origin}/api/platform/billing/webhook`}
            target="_blank"
          >
            Webhook URL
          </Button>
        </Stack>
      </Box>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Monthly Recurring Revenue"
            value={formatCurrency(overview?.mrr || 0)}
            icon={<MoneyIcon />}
            color={theme.palette.success.main}
            subtitle="All active subscriptions"
            trend={15}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Annual Recurring Revenue"
            value={formatCurrency(overview?.arr || 0)}
            icon={<TrendingUpIcon />}
            color={theme.palette.primary.main}
            subtitle="Projected yearly revenue"
            trend={18}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Active Subscriptions"
            value={overview?.activeSubscriptions || 0}
            icon={<CreditScore />}
            color={theme.palette.info.main}
            subtitle={`of ${overview?.totalTenants || 0} total tenants`}
            trend={8}
          />
        </Grid>
        <Grid item xs={12} sm={6} lg={3}>
          <MetricCard
            title="Churn Rate"
            value={`${overview?.churnRate || 0}%`}
            icon={<TrendingDown />}
            color={theme.palette.error.main}
            subtitle="Last 30 days"
            trend={-5}
          />
        </Grid>
      </Grid>

      {/* Revenue Breakdown */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Revenue by Plan
            </Typography>
            <Box sx={{ mt: 3 }}>
              {overview && Object.entries(overview.revenueByPlan).map(([plan, revenue]) => (
                <Box key={plan} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box display="flex" alignItems="center">
                      <Chip
                        label={plan.charAt(0).toUpperCase() + plan.slice(1)}
                        size="small"
                        color={getPlanColor(plan)}
                        sx={{ mr: 2 }}
                      />
                      <Typography variant="body2">{formatCurrency(revenue)}</Typography>
                    </Box>
                    <Typography variant="body2" fontWeight="600">
                      {overview.mrr > 0 ? Math.round((revenue / overview.mrr) * 100) : 0}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={overview.mrr > 0 ? (revenue / overview.mrr) * 100 : 0}
                    sx={{ 
                      height: 8,
                      borderRadius: 4,
                      backgroundColor: alpha(getPlanColorHex(plan), 0.2),
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getPlanColorHex(plan),
                        borderRadius: 4,
                      }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="500" gutterBottom>
              Growth Metrics
            </Typography>
            <Stack spacing={3} sx={{ mt: 3 }}>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">New Subscriptions</Typography>
                  <Typography variant="h6" color="success.main">+{overview?.growth.new || 0}</Typography>
                </Box>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Churned</Typography>
                  <Typography variant="h6" color="error.main">-{overview?.growth.churned || 0}</Typography>
                </Box>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Expansion Revenue</Typography>
                  <Typography variant="h6" color="primary.main">+{overview?.growth.expansion || 0}</Typography>
                </Box>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">Contraction</Typography>
                  <Typography variant="h6" color="warning.main">-{overview?.growth.contraction || 0}</Typography>
                </Box>
              </Box>
              <Divider />
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" fontWeight="600">Net Growth</Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="600"
                    color={(overview?.growth.new || 0) - (overview?.growth.churned || 0) >= 0 ? 'success.main' : 'error.main'}
                  >
                    {(overview?.growth.new || 0) - (overview?.growth.churned || 0) >= 0 ? '+' : ''}
                    {(overview?.growth.new || 0) - (overview?.growth.churned || 0)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </Paper>
        </Grid>
      </Grid>

      {/* Tabs for Subscriptions and Invoices */}
      <Paper sx={{ mb: 4 }}>
        <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)}>
          <Tab 
            label={
              <Box display="flex" alignItems="center" gap={1}>
                Subscriptions
                <Chip label={subscriptions.length} size="small" />
              </Box>
            } 
          />
          <Tab label="Invoices" />
          <Tab label="Payment Methods" />
          <Tab label="Settings" />
        </Tabs>
      </Paper>

      {/* Subscriptions Table */}
      {activeTab === 0 && (
        <Paper>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search tenants..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="all">All Status</MenuItem>
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="trialing">Trial</MenuItem>
                    <MenuItem value="past_due">Past Due</MenuItem>
                    <MenuItem value="canceled">Canceled</MenuItem>
                    <MenuItem value="suspended">Suspended</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth size="small">
                  <InputLabel>Plan</InputLabel>
                  <Select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    label="Plan"
                  >
                    <MenuItem value="all">All Plans</MenuItem>
                    <MenuItem value="starter">Starter</MenuItem>
                    <MenuItem value="growth">Growth</MenuItem>
                    <MenuItem value="professional">Professional</MenuItem>
                    <MenuItem value="enterprise">Enterprise</MenuItem>
                    <MenuItem value="custom">Custom</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Tenant</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Billing Cycle</TableCell>
                  <TableCell>Next Billing</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subscriptions
                  .filter(sub => searchTerm === '' || 
                    sub.tenantId?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    sub.tenantId?.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((subscription) => (
                    <TableRow key={subscription._id} hover>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <Avatar sx={{ mr: 2, bgcolor: alpha(theme.palette.primary.main, 0.1) }}>
                            <BusinessIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle2" fontWeight="500">
                              {subscription.tenantId?.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {subscription.tenantId?.subdomain}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={subscription.plan.charAt(0).toUpperCase() + subscription.plan.slice(1)}
                          size="small"
                          color={getPlanColor(subscription.plan)}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(subscription.status)}
                          <Chip
                            label={subscription.status.charAt(0).toUpperCase() + subscription.status.slice(1)}
                            size="small"
                            color={getStatusColor(subscription.status)}
                            variant="outlined"
                          />
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="600">
                          {formatCurrency(subscription.billing.totalAmount)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" textTransform="capitalize">
                          {subscription.billing.interval}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {new Date(subscription.schedule.nextBillingDate).toLocaleDateString()}
                          </Typography>
                          {subscription.schedule.trialEndDate && (
                            <Typography variant="caption" color="info.main">
                              Trial ends: {new Date(subscription.schedule.trialEndDate).toLocaleDateString()}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="View Details">
                            <IconButton 
                              size="small"
                              onClick={() => handleViewSubscription(subscription._id)}
                            >
                              <ViewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Manage Payment">
                            <IconButton 
                              size="small"
                              onClick={() => handleManagePayment(subscription._id)}
                            >
                              <PaymentIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit Subscription">
                            <IconButton size="small">
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={subscriptions.filter(sub => searchTerm === '' || 
              sub.tenantId?.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              sub.tenantId?.subdomain.toLowerCase().includes(searchTerm.toLowerCase())
            ).length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </Paper>
      )}

      {/* Other tabs content */}
      {activeTab === 1 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Invoice management coming soon
          </Typography>
        </Paper>
      )}

      {activeTab === 2 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            Payment method management coming soon
          </Typography>
        </Paper>
      )}

      {activeTab === 3 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight="500" gutterBottom>
            Platform Stripe Configuration
          </Typography>
          <Stack spacing={2} sx={{ mt: 3 }}>
            <Alert severity="info" icon={<InfoIcon />}>
              Configure your platform Stripe account to enable billing for all tenants
            </Alert>
            <TextField
              fullWidth
              label="Stripe Secret Key"
              placeholder="sk_live_..."
              type="password"
              helperText="Your platform's Stripe secret key"
            />
            <TextField
              fullWidth
              label="Stripe Publishable Key"
              placeholder="pk_live_..."
              helperText="Your platform's Stripe publishable key"
            />
            <TextField
              fullWidth
              label="Webhook Secret"
              placeholder="whsec_..."
              type="password"
              helperText="Stripe webhook endpoint secret"
            />
            <Box sx={{ mt: 2 }}>
              <Button variant="contained" sx={{ mr: 2 }}>
                Save Configuration
              </Button>
              <Button variant="outlined">
                Test Connection
              </Button>
            </Box>
          </Stack>
        </Paper>
      )}
    </Container>
  );
};

export default PlatformBillingDashboard;